import { useMemo } from 'react';
import type { MentionTarget } from '@/components/desktop-side-panel';
import { personaMentionTarget, useAgentPersonas } from '@/lib/conversations/agent-personas';
import {
  parseCommandQuery,
  parseMentionQuery,
  parseSkillQuery,
} from '@/lib/desktop-shell/composer-query';

/**
 * What the composer's picker reads from the current draft: the query after / ($ for a skill
 * alone) or @, and the references @ offers (the saved agents among them). Pure
 * per-conversation derivation, so the active pane and split panes compute their own.
 */
export function useComposerDerivations(input: {
  composerText: string;
  composerContextTargets: readonly MentionTarget[];
}): {
  commandQuery: string | null;
  skillQuery: string | null;
  mentionQuery: string | null;
  mentionTargets: readonly MentionTarget[];
} {
  // `@Designer` brings a saved agent in for one reply (design D51): the "Personas" source.
  const personas = useAgentPersonas();
  const mentionTargets = useMemo(
    () =>
      personas.length === 0
        ? input.composerContextTargets
        : [...input.composerContextTargets, ...personas.map(personaMentionTarget)],
    [input.composerContextTargets, personas],
  );
  return {
    commandQuery: parseCommandQuery(input.composerText),
    skillQuery: parseSkillQuery(input.composerText),
    mentionQuery: parseMentionQuery(input.composerText),
    mentionTargets,
  };
}
